import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { leitfadenStand, startStation } from './leitfaden';
import type { LeitfadenNavigation } from './leitfadenNavigation';
import { angezeigteStation, fixierteWahl, hilfeIstAus, type StationWahl } from './leitfadenRahmen';
import {
    blendeHilfeAus as merkeHilfeAus,
    hilfeAusgeblendet,
    ladeAnsicht,
    ladeMerker,
    merkeAnsicht,
    merkeStation,
} from './leitfadenSpeicher';
import { HILFE, type HilfeSchluessel } from './leitfadenTexte';
import type { PlanungAnsichtArt, StationSchluessel } from './leitfadenTypen';
import type { PlanungUnterlage, Planungsfrage, Planungshinweis, Planungsphase } from './types';

export interface LeitfadenEingabe {
    projectId: string;
    /** Der GESPEICHERTE Kopfsatz — nie der Entwurf der Grundlagen. Traegt
     *  auch den Vermerk „Rest bleibt offen" (`merkmale.leitfadenRestOffen`). */
    phase: Planungsphase | null;
    fragen: Planungsfrage[];
    hinweise: Planungshinweis[];
    /** Die Checkliste, wie sie gerade gilt (`useUnterlagenStand().liste`). */
    unterlagen: PlanungUnterlage[];
    heuteIso: string;
    /** Ausfuehrungsbeginn, Rueckfall Projektstart (`baustartAusGlobalData`). */
    baustartIso: string | null;
    /** Offene Fassungsfragen des Aenderungsdienstes (Vermerk an Station 9). */
    offeneFassungen: number;
    /** Eine KI-Auswertung laeuft (`PlanungAnsicht.kiLaeuft`): Station, Ablage
     *  und Ansicht wechseln dann nicht — jeder Wechsel haengte die KI-Leiste
     *  aus und braeche den Lauf ab. Die Knoepfe sind ohnehin aus; dies ist
     *  die zweite Reihe fuer Aufrufe, die erst spaeter ankommen. */
    wechselGesperrt: boolean;
}

const HILFE_SCHLUESSEL = Object.keys(HILFE) as HilfeSchluessel[];

const jetztIso = (): string => new Date().toISOString();

/**
 * Zustand des Leitfadens (Vertrag: `LeitfadenNavigation`).
 *
 * Der Stand der Stationen kommt immer aus den Daten (`leitfadenStand`); hier
 * liegt nur, was die Ansicht sich merkt: gewaehlte Ansicht, angezeigte
 * Station, offene Ablage, weggeklickte Hilfen. Der Browser-Speicher
 * (`leitfadenSpeicher.ts`) wird beim Oeffnen gelesen und bei jeder Handlung
 * geschrieben — nie in einem Effekt, nie im Zeichnen als Zustandswechsel.
 * Faellt er aus, gilt der Sitzungswert bzw. die errechnete Station.
 *
 * Die angezeigte Station FOLGT `startStation(merker, staende)`, bis der Nutzer
 * zum ersten Mal tippt oder klickt (`fixiere` am Rahmen, `geheZu`). Danach
 * steht sie: eine Antwort, die Station 6 erledigt, reisst ihn nicht mitten
 * im Satz auf Station 7.
 */
export const useLeitfaden = (p: LeitfadenEingabe): LeitfadenNavigation => {
    const { projectId, phase, fragen, hinweise, unterlagen, heuteIso, baustartIso, offeneFassungen, wechselGesperrt } = p;

    // Wechselsperre fuer Rueckrufe: im Effekt nachgefuehrt und nur dort
    // gelesen, nie im Zeichnen. „Speichern und weiter" ruft `geheZu` erst nach
    // dem Speichern — eine KI-Auswertung, die inzwischen begann, haelt es auf.
    const gesperrt = useRef(wechselGesperrt);
    useEffect(() => { gesperrt.current = wechselGesperrt; }, [wechselGesperrt]);

    // ─── Ansicht (je Browser) ────────────────────────────────────────────
    const [ansicht, setAnsichtZustand] = useState<PlanungAnsichtArt>(() => ladeAnsicht());
    const setAnsicht = useCallback((a: PlanungAnsichtArt) => {
        if (gesperrt.current) return;
        setAnsichtZustand(a);
        merkeAnsicht(a);
    }, []);

    // ─── Stand der Stationen ─────────────────────────────────────────────
    // „Rest bleibt offen" (Station 3) liest `leitfadenStand` selbst aus
    // `phase.merkmale` — dieselbe Regel wie Kachel und Dashboard.
    const staende = useMemo(
        () => leitfadenStand({ phase, fragen, hinweise, heuteIso, baustartIso, unterlagen, offeneFassungen }),
        [phase, fragen, hinweise, heuteIso, baustartIso, unterlagen, offeneFassungen],
    );

    // ─── Angezeigte Station ──────────────────────────────────────────────
    // Der Merker, wie er beim Oeffnen lag: einmal je Projekt gelesen, spaeteres
    // Merken aendert „Zuletzt hier" und die Startstation nicht mehr.
    const zuletzt = useMemo(() => ladeMerker(projectId), [projectId]);
    const [wahl, setWahl] = useState<StationWahl | null>(null);
    const station = angezeigteStation(wahl, projectId, startStation(zuletzt ? zuletzt.station : null, staende));

    // ─── Ablage ──────────────────────────────────────────────────────────
    // Je Projekt, wie die Wahl: offen heisst „offen fuer dieses Projekt".
    const [ablageFuer, setAblageFuer] = useState<string | null>(null);
    const ablageOffen = ablageFuer === projectId;
    const setAblageOffen = useCallback((offen: boolean) => {
        if (gesperrt.current) return;
        setAblageFuer(offen ? projectId : null);
    }, [projectId]);

    // Was gerade angezeigt wird — fuer Nacharbeiten, die erst nach einem
    // Speichervorgang laufen (`zeigtNoch`). Im Effekt nachgefuehrt und nur im
    // Rueckruf gelesen, nie im Zeichnen.
    const angezeigt = useRef({ station, ablageOffen });
    useEffect(() => { angezeigt.current = { station, ablageOffen }; }, [station, ablageOffen]);
    const zeigtNoch = useCallback(
        (s: StationSchluessel) => angezeigt.current.station === s && !angezeigt.current.ablageOffen,
        [],
    );

    const geheZu = useCallback((s: StationSchluessel) => {
        if (gesperrt.current) return;
        setWahl({ projectId, station: s });
        merkeStation(projectId, s, jetztIso());
        setAblageFuer(null);
    }, [projectId]);

    const fixiere = useCallback(() => {
        const neu = fixierteWahl(wahl, projectId, station);
        if (!neu) return;
        setWahl(neu);
        merkeStation(projectId, neu.station, jetztIso());
    }, [wahl, projectId, station]);

    // ─── Hilfekaesten ────────────────────────────────────────────────────
    // Der Speicher einmal beim Oeffnen; was der Nutzer danach wegklickt, steht
    // im Sitzungswert (und im Speicher fuers naechste Mal).
    const [hilfeGespeichert] = useState<ReadonlySet<string>>(
        () => new Set(HILFE_SCHLUESSEL.filter(k => hilfeAusgeblendet(k))),
    );
    const [hilfeSitzung, setHilfeSitzung] = useState<ReadonlySet<string>>(() => new Set());
    const hilfeAus = useCallback(
        (id: HilfeSchluessel) => hilfeIstAus(id, hilfeGespeichert, hilfeSitzung),
        [hilfeGespeichert, hilfeSitzung],
    );
    const blendeHilfeAus = useCallback((id: HilfeSchluessel) => {
        merkeHilfeAus(id);
        setHilfeSitzung(alt => new Set(alt).add(id));
    }, []);

    return useMemo<LeitfadenNavigation>(() => ({
        ansicht, setAnsicht,
        staende, station, geheZu, fixiere, zeigtNoch, zuletzt,
        ablageOffen, setAblageOffen,
        hilfeAus, blendeHilfeAus,
    }), [
        ansicht, setAnsicht,
        staende, station, geheZu, fixiere, zeigtNoch, zuletzt,
        ablageOffen, setAblageOffen,
        hilfeAus, blendeHilfeAus,
    ]);
};
